import { useState } from 'react';
import { X, Type, AlignLeft, Mail, Phone, List, ListChecks, Star } from 'lucide-react';
import type { FormFieldType } from '../../types/forms';

interface AddFieldModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (type: FormFieldType, label: string) => void;
}

const FIELD_TYPES: { type: FormFieldType; label: string; icon: typeof Type; description: string }[] = [
  { type: 'TEXT', label: 'Text', icon: Type, description: 'Einzeiliges Textfeld' },
  { type: 'TEXTAREA', label: 'Textfeld', icon: AlignLeft, description: 'Mehrzeiliges Textfeld' },
  { type: 'EMAIL', label: 'E-Mail', icon: Mail, description: 'E-Mail-Adresse mit Validierung' },
  { type: 'PHONE', label: 'Telefon', icon: Phone, description: 'Telefonnummer' },
  { type: 'SELECT', label: 'Auswahl', icon: List, description: 'Dropdown mit einer Option' },
  { type: 'MULTISELECT', label: 'Mehrfachauswahl', icon: ListChecks, description: 'Mehrere Optionen wählbar' },
  { type: 'RATING', label: 'Bewertung', icon: Star, description: '1-5 Sterne Bewertung' },
];

export function AddFieldModal({ isOpen, onClose, onAdd }: AddFieldModalProps) {
  const [selectedType, setSelectedType] = useState<FormFieldType | null>(null);
  const [label, setLabel] = useState('');

  if (!isOpen) return null;

  const handleAdd = () => {
    if (selectedType && label.trim()) {
      onAdd(selectedType, label.trim());
      setSelectedType(null);
      setLabel('');
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    setLabel('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 !mt-0">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Feld hinzufügen</h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!selectedType ? (
            <div className="grid grid-cols-2 gap-3">
              {FIELD_TYPES.map(({ type, label: typeLabel, icon: Icon, description }) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  className="flex flex-col items-start p-4 border border-gray-200 dark:border-slate-700 rounded-xl hover:border-primary hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors text-left"
                >
                  <Icon className="w-5 h-5 text-primary mb-2" />
                  <p className="font-medium text-gray-900 dark:text-white">{typeLabel}</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">{description}</p>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Bezeichnung des Feldes
                </label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="z.B. Vor- und Nachname"
                  className="w-full px-4 py-2.5 border border-gray-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-slate-700 text-[#1A1A1A] dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setSelectedType(null)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium"
                >
                  Zurück
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!label.trim()}
                  className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-dark transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Hinzufügen
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
